/**
 * Auth-related form schemas. Shared by login, password-reset, and
 * change-password screens (introduced for issue #688 — Login is the
 * pilot consumer).
 *
 * Server-side parity:
 *   - Email format: validated by Pydantic's `EmailStr`
 *     (`backend/app/models.py:UserLogin`). Front-end mirrors with
 *     `.email()`.
 *   - Password minimums: documented in
 *     `backend/app/auth_utils.py` / SECURITY.md. The schema below
 *     enforces a non-empty password client-side; the server enforces
 *     stronger rules on registration / reset.
 */

import { z } from 'zod';

export const LoginSchema = z.object({
  email: z
    .string()
    .min(1, 'メールアドレスを入力してください')
    .email('メールアドレスの形式が正しくありません'),
  password: z.string().min(1, 'パスワードを入力してください'),
});
export type LoginInput = z.infer<typeof LoginSchema>;

/** パスワード再設定メールの申請。ログインと同じメール規則 */
export const ForgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'メールアドレスを入力してください')
    .email('メールアドレスの形式が正しくありません'),
});
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

/**
 * 新しいパスワードの入力（再設定 / 変更）。
 * 「全て入力」「強度（8 文字・大小英字・数字）」「一致」は入力順に依存する
 * 直列チェックで、強度はブラウザの吹き出し（utils/passwordValidation）で出す
 * 契約のため、ここでは形だけを持ち、判定は各画面の送信処理が行う。
 */
export const NewPasswordSchema = z.object({
  newPassword: z.string(),
  confirmPassword: z.string(),
});
export type NewPasswordInput = z.infer<typeof NewPasswordSchema>;

export const ChangePasswordSchema = NewPasswordSchema.extend({
  currentPassword: z.string(),
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;
